import { atom, useAtomValue } from 'jotai'
import type { Atom } from 'jotai'
import { dateTextToDateTime, format, subDays as subDaysDateText } from '~/lib/date/date'
import {
  dateTimeToDateText,
  getEndOfDay,
  isSameDay,
  isWithinInterval,
  subDays as subDaysDateTime,
} from '~/lib/date/dateTime'
import type { DateText, DateTime } from '~/lib/graphql/scalars'
import { money } from '~/lib/money/money'
import { now$ } from '~/stores/now'
import { visitDate$ } from './stateMachine/context'
import type {
  ReserveInputPolicies,
  ReserveInputPolicy,
  ReserveInputRestaurant,
} from './stateMachine/state'
import { policies$, restaurant$ } from './stateMachine/state'

const cancelLimitDay$ = atom((get) => {
  return getCancelLimitDay({ restaurant: get(restaurant$), visitDate: get(visitDate$) })
})
cancelLimitDay$.debugLabel = 'cancelLimitDay$'

function getCancelLimitDay(data: { restaurant?: ReserveInputRestaurant; visitDate?: DateText }) {
  if (data.restaurant === undefined || data.visitDate === undefined) {
    return undefined
  }
  // noncancelableDays を引いた日付の 00:00:00 から操作できなくなるので、
  // 操作可能なのはさらに1日分引いた日付が操作限界日になる
  // 例) 予約日が 2024-03-03 で noncancelableDays が 2 なら
  //     2024-03-01 00:00:00 から操作できなくなるので 2024-02-29 以前がキャンセル可能な日付
  return subDaysDateText(data.visitDate, data.restaurant.noncancelableDays + 1)
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('getCancelLimitDay', () => {
    expect(getCancelLimitDay({})).toBe(undefined)
    expect(getCancelLimitDay({ visitDate: '2024-01-01' as DateText })).toBe(undefined)
    expect(
      getCancelLimitDay({ restaurant: { noncancelableDays: 1 } as ReserveInputRestaurant })
    ).toBe(undefined)
    expect(
      getCancelLimitDay({
        restaurant: { noncancelableDays: 1 } as ReserveInputRestaurant,
        visitDate: '2024-01-01' as DateText,
      })
    ).toBe('2023-12-30')
  })
}

type CancelPolicyStatus =
  | {
      type: 'cancellable'
      limitDay: string
    }
  | { type: 'uncancellable' }

const cancelPolicyStatus$: Atom<CancelPolicyStatus> = atom((get) => {
  const limitDay = get(cancelLimitDay$)
  if (limitDay === undefined) {
    return { type: 'uncancellable' }
  }
  return isCancelable({ restaurant: get(restaurant$), visitDate: get(visitDate$), now: get(now$) })
    ? { type: 'cancellable', limitDay: format(limitDay, 'M月d日(E)') }
    : { type: 'uncancellable' }
})
cancelPolicyStatus$.debugLabel = 'cancelPolicyStatus$'

export function useCancelPolicyStatus() {
  return useAtomValue(cancelPolicyStatus$)
}

function isCancelable(data: {
  restaurant?: ReserveInputRestaurant
  visitDate?: DateText
  now?: DateTime
}) {
  const cancelLimitDay = getCancelLimitDay({
    restaurant: data.restaurant,
    visitDate: data.visitDate,
  })
  if (data.now === undefined || cancelLimitDay === undefined) {
    return false
  }
  return cancelLimitDay >= dateTimeToDateText(data.now).dateText
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('isCancelable', () => {
    expect(
      isCancelable({
        restaurant: { noncancelableDays: 1 } as ReserveInputRestaurant,
        visitDate: '2024-01-01' as DateText,
        now: '2023-12-31T09:00:00.000+09:00' as DateTime,
      })
    ).toBe(false)
    expect(
      isCancelable({
        restaurant: { noncancelableDays: 1 } as ReserveInputRestaurant,
        visitDate: '2024-01-01' as DateText,
        now: '2023-12-30T09:00:00.000+09:00' as DateTime,
      })
    ).toBe(true)
  })
}

const cancelFeeStartDateTime$ = atom((get) => {
  return getCancelFeeStartDateTime({ policies: get(policies$), visitDate: get(visitDate$) })
})
cancelFeeStartDateTime$.debugLabel = 'cancelFeeStartDateTime$'

export function useCancelFeeStartDateTime() {
  return useAtomValue(cancelFeeStartDateTime$)
}

function getCancelFeeStartDateTime(data: { policies: ReserveInputPolicies; visitDate?: DateText }) {
  if (data.policies === undefined || data.visitDate === undefined) {
    return undefined
  }

  // キャンセル料金が発生する最初のポリシーを取得
  const policy = data.policies.findLast(
    (policy) =>
      (policy.charge.__typename === 'CancelPolicyChargeFee' && policy.charge.fee > 0) ||
      (policy.charge.__typename === 'CancelPolicyChargeRate' && policy.charge.rate > 0)
  )
  if (policy === undefined || Object.keys(policy).length === 0) {
    return undefined
  }

  // キャンセル料金がn日前から発生する場合その日時を入れる
  if (policy.__typename === 'CancelPolicyItemDaysBefore') {
    return subDaysDateTime(
      dateTextToDateTime(data.visitDate, policy.time ?? undefined),
      policy.days
    )
  } else {
    // キャンセル料金が当日から発生する場合は予約対象日（時間は00:00）を入れる
    return dateTextToDateTime(data.visitDate)
  }
}
if (import.meta.vitest) {
  const { expect, it, describe } = import.meta.vitest
  describe('getCancelFeeStartDateTime', () => {
    expect(getCancelFeeStartDateTime({ policies: [] })).toBe(undefined)
    expect(getCancelFeeStartDateTime({ policies: [], visitDate: '2024-01-01' as DateText })).toBe(
      undefined
    )
    it('来店日から2日前にキャンセル料が発生する', () => {
      expect(
        getCancelFeeStartDateTime({
          policies: [
            {
              __typename: 'CancelPolicyItemDaysBefore',
              days: 1,
              time: '09:00',
              charge: { __typename: 'CancelPolicyChargeFee', fee: 1000 },
            },
            {
              __typename: 'CancelPolicyItemDaysBefore',
              days: 2,
              time: '09:00',
              charge: { __typename: 'CancelPolicyChargeFee', fee: 500 },
            },
          ] as ReserveInputPolicies,
          visitDate: '2024-01-01' as DateText,
        })
      ).toBe('2023-12-30T09:00:00.000+09:00')
    })
    it('予約当日からキャンセル料が発生する', () => {
      expect(
        getCancelFeeStartDateTime({
          policies: [
            {
              __typename: 'CancelPolicyItemOnTheDay',
              charge: { __typename: 'CancelPolicyChargeFee', fee: 1000 },
              appliesToVisitorDecrement: true,
            },
          ] as ReserveInputPolicies,
          visitDate: '2024-01-01' as DateText,
        })
      ).toBe('2024-01-01T00:00:00.000+09:00')
    })
  })
}

const isCancelFeeApplicable$ = atom((get) => {
  return isCancelFeeApplicable({
    cancelFeeStartDateTime: get(cancelFeeStartDateTime$),
    visitDate: get(visitDate$),
    now: get(now$),
  })
})
isCancelFeeApplicable$.debugLabel = 'isCancelFeeApplicable$'

export function useIsSubjectToCancelFee() {
  return useAtomValue(isCancelFeeApplicable$)
}

function isCancelFeeApplicable(data: {
  cancelFeeStartDateTime?: DateTime
  visitDate?: DateText
  now?: DateTime
}) {
  if (
    data.cancelFeeStartDateTime === undefined ||
    data.visitDate === undefined ||
    data.now === undefined
  ) {
    return false
  }
  const visitDateTime = dateTextToDateTime(data.visitDate)
  // キャンセル料金発生範囲の最終日（予約日の23時59分59秒）
  const cancelFeeRangeEnd = getEndOfDay(visitDateTime)
  return (
    isWithinInterval(data.now, data.cancelFeeStartDateTime, cancelFeeRangeEnd) ||
    isSameDay(data.now, visitDateTime)
  )
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('isCancelFeeApplicable', () => {
    const cancelFeeStartDateTime = '2024-01-01T00:00:00.000+09:00' as DateTime
    const visitDate = '2024-01-03' as DateText
    expect(
      isCancelFeeApplicable({
        cancelFeeStartDateTime,
        visitDate,
        now: '2024-12-31T23:59:59.999+09:00' as DateTime,
      })
    ).toBe(false)
    expect(
      isCancelFeeApplicable({
        cancelFeeStartDateTime,
        visitDate,
        now: '2024-01-01T00:00:00.000+09:00' as DateTime,
      })
    ).toBe(true)
    expect(
      isCancelFeeApplicable({
        cancelFeeStartDateTime,
        visitDate,
        now: '2024-01-02T00:00:00.000+09:00' as DateTime,
      })
    ).toBe(true)
    expect(
      isCancelFeeApplicable({
        cancelFeeStartDateTime,
        visitDate,
        now: '2024-01-02T23:59:59.999+09:00' as DateTime,
      })
    ).toBe(true)
  })
}

export type DisplayCancelPolicy = {
  condition: string
  penalty: string
  appliesToVisitorDecrement: boolean
}

const displayCancelPolicies$: Atom<DisplayCancelPolicy[] | []> = atom((get) => {
  const policies = get(policies$)
  if (policies === undefined) {
    return []
  }
  // appliesToVisitorDecrement は 予約変更時「も」キャンセルポリシーを表示させるフラグなので、
  // 予約取消は appliesToVisitorDecrement 関係なくキャンセルポリシーを全て表示する
  return (
    policies
      .map((p) => ({
        condition: createCancelPolicyCondition(p),
        penalty: createCancelPolicyPenalty(p),
        appliesToVisitorDecrement: p.appliesToVisitorDecrement,
      }))
      // デフォルトでポリシーは当日連絡あり／なし、ポリシー1、2…の順に並んでいる。
      // 直近のキャンセル料金発生日から表示するとユーザーにとって自然なので、逆順にする
      .reverse()
  )
})
displayCancelPolicies$.debugLabel = 'displayCancelPolicies$'

export function useDisplayCancelPolicies() {
  return useAtomValue(displayCancelPolicies$)
}

function createCancelPolicyCondition(policy: ReserveInputPolicy) {
  if (policy.__typename === 'CancelPolicyItemNoShow') {
    return '当日連絡なし'
  } else if (policy.__typename === 'CancelPolicyItemOnTheDay') {
    return '当日連絡あり'
  } else if (policy.__typename === 'CancelPolicyItemDaysBefore') {
    return `${policy.days}日前${policy.time === null ? `` : `${policy.time}〜`} `
  }
  return ''
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('createCancelPolicyLabel', () => {
    expect(
      createCancelPolicyCondition({
        __typename: 'CancelPolicyItemNoShow',
      } as ReserveInputPolicy)
    ).toBe('当日連絡なし')
    expect(
      createCancelPolicyCondition({
        __typename: 'CancelPolicyItemOnTheDay',
      } as ReserveInputPolicy)
    ).toBe('当日連絡あり')
    expect(
      createCancelPolicyCondition({
        __typename: 'CancelPolicyItemDaysBefore',
        days: 1,
        time: '09:00',
      } as ReserveInputPolicy)
    ).toBe('1日前09:00〜 ')
    expect(
      createCancelPolicyCondition({
        __typename: 'CancelPolicyItemDaysBefore',
        days: 1,
        time: null,
      } as ReserveInputPolicy)
    ).toBe('1日前 ')
  })
}

function createCancelPolicyPenalty(policy: ReserveInputPolicy) {
  if (policy.charge.__typename === 'CancelPolicyChargeRate') {
    return `${policy.charge.rate}%`
  } else if (policy.charge.__typename === 'CancelPolicyChargeFee') {
    return `${money(policy.charge.fee)}円`
  }
  return ''
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('createCancelPolicyPriceOrFee', () => {
    expect(
      createCancelPolicyPenalty({
        charge: { __typename: 'CancelPolicyChargeRate', rate: 10 },
      } as ReserveInputPolicy)
    ).toBe('10%')
    expect(
      createCancelPolicyPenalty({
        charge: { __typename: 'CancelPolicyChargeFee', fee: 1000 },
      } as ReserveInputPolicy)
    ).toBe('1,000円')
  })
}

const displayDecrementVisitorPolicies$: Atom<DisplayCancelPolicy[] | []> = atom((get) => {
  const policies = get(displayCancelPolicies$)
  return policies.filter((p) => p.appliesToVisitorDecrement)
})
displayDecrementVisitorPolicies$.debugLabel = 'displayDecrementVisitorPolicies$'

export function useDisplayDecrementVisitorPolicies() {
  return useAtomValue(displayDecrementVisitorPolicies$)
}
